<template>
  <div>
    <v-btn
      v-if="buttunWithText"
      color="primary"
      @click="generatePDF"
      :disabled="!invoice"
      dark
      large
    >
      <v-icon left dark>{{ printIcon }}</v-icon> {{ btnLabel }}
    </v-btn>
    <v-btn
      v-else
      :style="gradientIconStyle"
      icon
      @click="generatePDF"
      :disabled="!invoice"
      dark
      large
    >
      <v-icon left dark>{{ printIcon }}</v-icon>
    </v-btn>

    <!-- Mobile PDF Viewer Dialog -->
    <v-dialog
      v-model="mobileDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="d-flex flex-column fill-height">
        <!-- Toolbar -->
        <v-toolbar dark :color="getPrimaryColor">
          <v-btn icon dark @click="mobileDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>PDF Preview</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="downloadPdf">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Viewer Controls -->
        <v-card-text class="flex-grow-1 overflow-y-auto text-center pa-4">
          <div class="d-flex align-center justify-center mb-4">
            <v-btn icon @click="prevPage" :disabled="currentPage <= 1">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="mx-2">Page {{ currentPage }} of {{ totalPages }}</span>
            <v-btn icon @click="nextPage" :disabled="currentPage >= totalPages">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-divider vertical class="mx-3"></v-divider>
            <v-btn icon @click="zoomOut" :disabled="zoom <= 0.5">
              <v-icon>mdi-magnify-minus-outline</v-icon>
            </v-btn>
            <v-btn icon @click="zoomIn" :disabled="zoom >= 2.0">
              <v-icon>mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </div>

          <!-- Canvas Container -->
          <div class="pdf-canvas-wrapper d-flex justify-center">
            <div
              class="pdf-canvas-wrapper d-flex justify-center"
              v-if="pdfReady"
            >
              <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>
            </div>
          </div>
        </v-card-text>

        <!-- Sticky Download Action Footer -->
        <v-card-actions class="pa-4 grey lighten-4">
          <v-btn block large color="success" @click="downloadPdf">
            <v-icon left>mdi-download</v-icon>
            Download PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import companiesJs from "@/plugins/companies";
import pdfMakeParts from "@/plugins/pdfMakeParts";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import pdfMakeAutoHeaderTemplates from "@/pdfMakeHeaderTemplates/pdfMakeAutoHeaderTemplates";

// 1. Correct Virtual File System assignment for pdfMake
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).toString();

export default {
  name: "ProFormaGenerator",
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: "TAX INVOICE",
    },
    buttunWithText: {
      default: false,
    },
  },
  computed: {
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    gradientIconStyle() {
      return this.$store.getters.gradientIconStyle;
    },
    getTrustAccLabel() {
      return this.$store.getters.getTrustAccLabel;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getStamp() {
      return this.$store.getters.getStamp;
    },
    getAppMode() {
      return this.$store.getters.getAppMode;
    },
    getTableName() {
      return this.getAppMode === "privatePractice"
        ? "patient_files"
        : "social_work_files";
    },
    getAppModeFlags() {
      return this.$store.getters.getAppModeFlags;
    },
    getAllCases() {
      return this.$store.getters.getAllCases;
    },
    getAllAutoServices() {
      return this.$store.getters.getAllAutoServices;
    },
    getAllProjects() {
      return this.$store.getters.getAllProjects;
    },
    getAllPractices() {
      return this.$store.getters.getAllPractices;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    getPrimaryColor() {
      return this.$store.getters.getPrimaryColor;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
  },
  watch: {
    isMobile() {
      this.setBtnLabels();
    },
  },
  data: () => ({
    btnLabel: "Print",
    printIcon: "mdi-printer",
    caseData: null,
    pdfDataUrl: null,
    loading: false,
    pdfDoc: null,
    totalPages: 0,
    currentPage: 1,
    zoom: 1.0,
    pdfReady: false,
    pdfBlob: null,
    pdfDefinition: null,
    mobileDialog: false, // Dialog state control
    renderTask: null,
  }),
  mounted() {
    this.setBtnLabels();
  },
  methods: {
    setBtnLabels() {
      if (this.isMobile) {
        this.btnLabel = "Download";
        this.printIcon = "mdi-download";
      } else {
        this.btnLabel = "Print";
        this.printIcon = "mdi-printer";
      }
    },
    formatCurrency: function (val) {
      const num = Number(val);

      // Guard against null, undefined, NaN, or non-numeric strings
      if (isNaN(num)) {
        return `${this.getCurrency || ""} 0.00`.trim();
      }

      return `${this.getCurrency || ""} ${num.toFixed(2)}`.trim();
    },
    async generatePDF() {
      if (!this.invoice) return;
      const itsAnInvoice = this.invoice.quote_number ? false : true;

      // 1. Safely fetch stamp Base64 string
      let stampBase64 = null;
      try {
        const logoUrl = this.getStamp ?? null;
        if (logoUrl) {
          //stampBase64 = await this.getBase64FromUrl(logoUrl);
          stampBase64 = await pdfMakeParts.getRoundedBase64Logo(logoUrl, 40);
        }
      } catch (e) {
        console.warn("Stamp could not be loaded for PDF", e);
      }

      const invoiceOrQuoteNumber =
        this.invoice.quote_number ||
        this.invoice.invoice_number ||
        this.invoice.invoice_no;

      let appName = "BitsInvoice";
      let appSite = "bitsinvoice.co.za";

      this.$store.commit("setLoader", true);
      const company = await companiesJs.initialiseCompanyAndLogo();
      this.$store.commit("setLoader", false);

      if (this.getAppModeFlags.itsLegal) {
        appName = "Legal Case Billing";
        appSite = "legalcasebilling.co.za";
        if (itsAnInvoice) {
          this.$store.commit("setLoader", true);
          this.caseData = await this.$store.dispatch(
            "getLegalCaseByInvoiceNumber",
            { invoiceNumber: this.invoice.invoice_no }
          );
          this.$store.commit("setLoader", false);
          if (!this.caseData) {
            return;
          }
        } else {
          this.caseData = this.invoice;
        }
      } else if (this.getAppModeFlags.itsAuto) {
        if (itsAnInvoice) {
          this.$store.commit("setLoader", true);
          this.caseData = await this.$store.dispatch(
            "getAutoServiceByInvoice",
            { invoiceNumber: this.invoice.invoice_number }
          );
          this.$store.commit("setLoader", false);
          if (!this.caseData) {
            return;
          }
        } else {
          this.caseData = this.invoice;
        }
      } else if (
        this.getAppModeFlags.itsProjects ||
        this.getAppModeFlags.itsEvents
      ) {
        if (itsAnInvoice) {
          this.$store.commit("setLoader", true);
          this.caseData = this.$store.dispatch("getProjectByInvoiceNumber", {
            invoiceNumber: this.invoice.invoice_number,
          });
          this.$store.commit("setLoader", false);
          if (!this.caseData) {
            return;
          }
        } else {
          this.caseData = this.invoice;
        }
      } else if (
        this.getAppModeFlags.itsPractice ||
        this.getAppModeFlags.itsSocial
      ) {
        if (itsAnInvoice) {
          this.$store.commit("setLoader", true);
          this.caseData = this.$store.dispatch("getPracticeByInvoiceNumber", {
            table: this.getTableName,
            invoiceNumber: this.invoice.invoice_number,
          });
          this.$store.commit("setLoader", false);
          if (!this.caseData) {
            return;
          }
        } else {
          this.caseData = this.invoice;
        }
      } else {
        //General Industry
        if (itsAnInvoice) {
          this.$store.commit("setLoader", true);
          this.caseData = await this.$store.dispatch(
            "getGenInvoiceByInvoiceNumber",
            { invoiceNumber: this.invoice.invoice_number }
          );
          this.$store.commit("setLoader", false);
          if (!this.caseData) {
            return;
          }
        } else {
          this.caseData = this.invoice;
        }
      }

      const data = this.caseData;
      const invoiceProp = this.invoice;

      const allPayments = data?.allPayments || [];
      const trustLedger = data?.trust_ledger || [];
      const primaryColor = this.getPrimaryColor || "#1A365D";
      const item_date =
        data?.instruction_date ||
        invoiceProp?.invoice_date ||
        invoiceProp?.quote_date ||
        data?.created_at ||
        "";

      let header = await pdfMakeParts.generatePdfHeaderCompact(
        this.title,
        company,
        invoiceProp?.client,
        primaryColor,
        item_date
      );
      let subheader = {};

      if (this.getAppModeFlags.itsAuto) {
        //subheader = pdfMakeParts.generateClientSubHeaderAuto(this.title, data); //generic for all
        header = await pdfMakeAutoHeaderTemplates.generatePdfHeaderCompact_1(
          this.title,
          company,
          invoiceProp?.client,
          primaryColor,
          item_date
        );
        subheader = pdfMakeAutoHeaderTemplates.generateClientSubHeaderAuto_1(
          this.title,
          data
        );
      } else if (this.getAppModeFlags.itsLegal) {
        subheader = pdfMakeParts.generateClientSubHeaderCase(this.title, data);
      } else if (this.getAppModeFlags.itsProjects) {
        subheader = pdfMakeParts.generateClientSubHeaderProject(
          this.title,
          data
        );
      } else if (this.getAppModeFlags.itsEvents) {
        subheader = pdfMakeParts.generateClientSubHeaderEvent(this.title, data);
      } else if (this.getAppModeFlags.itsPractice) {
        subheader = pdfMakeParts.generatePrivatePracticeSubHeader(
          this.title,
          data
        );
      } else if (this.getAppModeFlags.itsSocial) {
        subheader = pdfMakeParts.generateSocialWorkSubHeader(this.title, data);
      } else {
        subheader = pdfMakeParts.generateClientSubHeaderGeneral(
          this.title,
          data
        );
      }

      // Table calculations
      var body = [
        [
          { text: "DATE", style: "tableHeader" },
          { text: "DESCRIPTION / SERVICE", style: "tableHeader" },
          { text: "QTY", style: "tableHeader", alignment: "center" },
          { text: "AMOUNT (EXCL)", style: "tableHeader", alignment: "right" },
          { text: "VAT", style: "tableHeader", alignment: "right" },
          { text: "AMOUNT", style: "tableHeader", alignment: "right" },
        ],
      ];

      var subTotalExcl = 0;
      var totalVAT = 0;
      var entries = data?.billingEntries || invoiceProp?.billing_entries || [];

      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var rawDate = entry.created_at || entry.updated_at || "";
        var date = rawDate ? rawDate.split(" ")[0] : "N/A";
        var description = entry.name || "Service Item";
        var qty = Number(entry.quantity) || 1;
        var price = Number(entry.price) || 0;
        var lineTotal = Number(entry.lineTotal) || price * qty;
        var vat = Number(entry.tax_amount) || 0;

        subTotalExcl += lineTotal;
        totalVAT += vat;
        var rowBg = i % 2 === 0 ? "#FFFFFF" : "#F9FAFB";

        body.push([
          { text: date, fillColor: rowBg },
          { text: description, fillColor: rowBg, bold: true },
          { text: qty.toString(), alignment: "center", fillColor: rowBg },
          {
            text: this.formatCurrency(price),
            alignment: "right",
            fillColor: rowBg,
          },
          {
            text: this.formatCurrency(vat),
            alignment: "right",
            fillColor: rowBg,
          },
          {
            text: this.formatCurrency(lineTotal + vat),
            alignment: "right",
            fillColor: rowBg,
            bold: true,
          },
        ]);
      }

      const grandTotalBilled = subTotalExcl + totalVAT;

      // Upfront or Trust calculations
      var trustBody = [
        [
          { text: "DATE", style: "tableHeader" },
          { text: "DESCRIPTION", style: "tableHeader" },
          { text: "METHOD", style: "tableHeader" },
          { text: "TYPE", style: "tableHeader" },
          { text: "AMOUNT", style: "tableHeader", alignment: "right" },
        ],
      ];

      let calculatedTrustBalance = 0;

      for (let j = 0; j < trustLedger.length; j++) {
        const ledgerItem = trustLedger[j];
        const ledgerDate = ledgerItem?.transaction_at
          ? ledgerItem.transaction_at.split(" ")[0]
          : "N/A";
        const itemAmount = Number(ledgerItem?.amount) || 0;
        const itemType = (ledgerItem?.type || "").trim().toLowerCase();

        if (itemType === "deposit" || itemType === "credit") {
          calculatedTrustBalance += itemAmount;
        } else if (
          ["disbursement", "withdrawal", "transfer", "debit", "fee"].includes(
            itemType
          )
        ) {
          calculatedTrustBalance -= itemAmount;
        } else {
          calculatedTrustBalance += itemAmount;
        }

        const trustRowBg = j % 2 === 0 ? "#FFFFFF" : "#F9FAFB";
        trustBody.push([
          { text: ledgerDate, fillColor: trustRowBg },
          { text: ledgerItem.description || "N/A", fillColor: trustRowBg },
          { text: ledgerItem.payment_method || "N/A", fillColor: trustRowBg },
          { text: ledgerItem.type || "N/A", fillColor: trustRowBg, bold: true },
          {
            text: this.formatCurrency(itemAmount),
            alignment: "right",
            fillColor: trustRowBg,
          },
        ]);
      }

      // Payments calculations
      var paymentsBody = [
        [
          { text: "DATE", style: "tableHeader" },
          { text: "DESCRIPTION", style: "tableHeader" },
          { text: "METHOD", style: "tableHeader" },
          { text: "TYPE", style: "tableHeader" },
          { text: "AMOUNT", style: "tableHeader", alignment: "right" },
        ],
      ];

      let calculatedAllPaymentsBalance = 0;

      for (let z = 0; z < allPayments.length; z++) {
        const paymentItem = allPayments[z];

        const paymentDate = paymentItem?.date_paid
          ? paymentItem.date_paid
          : "N/A";
        const itemAmount = Number(paymentItem?.amount_paid) || 0;
        const itemType = "payment"; //(paymentItem.type || "").trim().toLowerCase();

        if (itemType === "payment" || itemType === "credit") {
          calculatedAllPaymentsBalance += itemAmount;
        } else if (
          ["disbursement", "withdrawal", "transfer", "debit", "fee"].includes(
            itemType
          )
        ) {
          calculatedAllPaymentsBalance -= itemAmount;
        } else {
          calculatedAllPaymentsBalance += itemAmount;
        }

        const paymentRowBg = z % 2 === 0 ? "#FFFFFF" : "#F9FAFB";
        paymentsBody.push([
          { text: paymentDate, fillColor: paymentRowBg },
          { text: paymentItem?.description || "N/A", fillColor: paymentRowBg },
          {
            text: paymentItem?.payment_method || "N/A",
            fillColor: paymentRowBg,
          },
          { text: itemType || "N/A", fillColor: paymentRowBg, bold: true },
          {
            text: this.formatCurrency(itemAmount),
            alignment: "right",
            fillColor: paymentRowBg,
          },
        ]);
      }

      const trustLabel = this.getTrustAccLabel.toLowerCase();
      console.log("trustLabel: ", trustLabel);
      const allRecordedPayments = data?.allPayments || data?.all_payments || [];

      const directPayments = allRecordedPayments
        .filter(
          (pay) => !pay?.payment_method?.toLowerCase().includes(trustLabel)
        )
        .reduce((acc, pay) => acc + (Number(pay?.amount_paid) || 0), 0);

      const trustApplied = allRecordedPayments
        .filter((pay) =>
          pay?.payment_method?.toLowerCase().includes(trustLabel)
        )
        .reduce((acc, pay) => acc + (Number(pay?.amount_paid) || 0), 0);

      const remainingUnpaid = Math.max(
        0,
        grandTotalBilled - directPayments - trustApplied
      );
      const netAmountDue = Math.max(0, remainingUnpaid);
      const remainingTrust = Math.max(0, calculatedTrustBalance);

      const summaryRows = [
        // [
        //   { text: "Subtotal (Excl VAT):", style: "summaryLabel" },
        //   {
        //     text: this.formatCurrency(subTotalExcl),
        //     alignment: "right",
        //     style: "summaryVal",
        //   },
        // ],
        // [
        //   { text: "Total VAT (15%):", style: "summaryLabel" },
        //   {
        //     text: this.formatCurrency(totalVAT),
        //     alignment: "right",
        //     style: "summaryVal",
        //   },
        // ],
        // [
        //   {
        //     text: `TOTAL ${
        //       this.title.toLowerCase().includes("invoice")
        //         ? "INVOICE"
        //         : "QUOTATION"
        //     } AMOUNT:`,
        //     style: "summaryBoldLabel",
        //   },
        //   {
        //     text: this.formatCurrency(grandTotalBilled),
        //     alignment: "right",
        //     style: "summaryBoldVal",
        //   },
        // ],
      ];

      if (directPayments > 0) {
        summaryRows.push([
          { text: "Direct Payments Received:", style: "summaryLabel" },
          {
            text: "- " + this.formatCurrency(directPayments),
            alignment: "right",
            style: "summaryVal",
          },
        ]);
      }

      if (trustApplied > 0) {
        summaryRows.push([
          {
            text: `${this.getTrustAccLabel.toUpperCase()} Account Funds Applied:`,
            style: "trustSummaryLabel",
          },
          {
            text: "- " + this.formatCurrency(trustApplied),
            alignment: "right",
            style: "trustSummaryVal",
          },
        ]);
      }

      summaryRows.push([
        { text: "NET AMOUNT DUE FROM CLIENT:", style: "netDueLabel" },
        {
          text: this.formatCurrency(netAmountDue),
          alignment: "right",
          style: "netDueVal",
        },
      ]);

      if (calculatedTrustBalance > 0) {
        summaryRows.push([
          {
            text: `REMAINING ${this.getTrustAccLabel.toUpperCase()} BALANCE:`,
            style: "trustSummaryLabel",
            bold: true,
          },
          {
            text: this.formatCurrency(remainingTrust),
            alignment: "right",
            style: "trustSummaryVal",
            bold: true,
          },
        ]);
      }

      const docDefinition = {
        pageSize: "A4",
        pageMargins: [35, 45, 35, 50],
        content: [
          header,
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 8,
                x2: 525,
                y2: 8,
                lineWidth: 1.5,
                lineColor: primaryColor,
              },
            ],
          },
          { text: "\n" },
          subheader,
          { text: "\n" },
          { text: "FEE & BILLING BREAKDOWN", style: "sectionTitle" },
          {
            table: {
              headerRows: 1,
              widths: ["16%", "*", "8%", "18%", "13%", "22%"],
              body: body,
            },
            layout: {
              hLineWidth: function (i, node) {
                return i === 0 || i === node.table.body.length ? 1 : 0.5;
              },
              vLineWidth: function () {
                return 0;
              },
              hLineColor: function () {
                return "#E2E8F0";
              },
              paddingTop: function () {
                return 6;
              },
              paddingBottom: function () {
                return 6;
              },
            },
          },
          { text: "\n" },
          {
            columns: [
              { text: "", width: "*" },
              {
                width: 240,
                table: {
                  widths: ["*", "auto"],
                  body: [
                    [
                      { text: "Subtotal (Excl VAT):", style: "summaryLabel" },
                      {
                        text: this.formatCurrency(subTotalExcl),
                        alignment: "right",
                        style: "summaryVal",
                      },
                    ],
                    [
                      { text: "Total VAT (15%):", style: "summaryLabel" },
                      {
                        text: this.formatCurrency(totalVAT),
                        alignment: "right",
                        style: "summaryVal",
                      },
                    ],
                    [
                      {
                        text: `TOTAL ${
                          this.title.toLowerCase().includes("invoice")
                            ? "INVOICE"
                            : "QUOTATION"
                        } AMOUNT:`,
                        style: "summaryBoldLabel",
                      },
                      {
                        text: this.formatCurrency(grandTotalBilled),
                        alignment: "right",
                        style: "summaryBoldVal",
                      },
                    ],
                  ],
                },
                layout: {
                  paddingTop: function () {
                    return 4;
                  },
                  paddingBottom: function () {
                    return 4;
                  },
                  hLineWidth: function (i, node) {
                    return i === node.table.body.length - 1 ? 1 : 0;
                  },
                  hLineColor: function () {
                    return "#CBD5E1";
                  },
                },
              },
            ],
          },
          trustLedger.length
            ? [
                // { text: "\n\n" },
                {
                  text: `${this.getTrustAccLabel.toUpperCase()} ACCOUNT LEDGER STATEMENT`,
                  style: "sectionTitle",
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ["15%", "*", "18%", "15%", "20%"],
                    body: trustBody,
                  },
                  layout: {
                    hLineWidth: function (i, node) {
                      return i === 0 || i === node.table.body.length ? 1 : 0.5;
                    },
                    vLineWidth: function () {
                      return 0;
                    },
                    hLineColor: function () {
                      return "#E2E8F0";
                    },
                    paddingTop: function () {
                      return 5;
                    },
                    paddingBottom: function () {
                      return 5;
                    },
                  },
                },
                { text: "\n" },
                // {
                //   columns: [
                //     { text: "", width: "*" },
                //     {
                //       width: 250,
                //       table: {
                //         widths: ["*", "auto"],
                //         body: summaryRows,
                //       },
                //       layout: {
                //         paddingTop: function () {
                //           return 4;
                //         },
                //         paddingBottom: function () {
                //           return 4;
                //         },
                //         hLineWidth: function (i, node) {
                //           return i === node.table.body.length - 1 ? 1.5 : 0;
                //         },
                //         hLineColor: function () {
                //           return primaryColor;
                //         },
                //       },
                //     },
                //   ],
                // },
              ]
            : [],
          allPayments.length
            ? [
                // { text: "\n\n" },
                {
                  text: `ALL PAYMENTS APPLIED`,
                  style: "sectionTitle",
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ["15%", "*", "18%", "15%", "20%"],
                    body: paymentsBody,
                  },
                  layout: {
                    hLineWidth: function (i, node) {
                      return i === 0 || i === node.table.body.length ? 1 : 0.5;
                    },
                    vLineWidth: function () {
                      return 0;
                    },
                    hLineColor: function () {
                      return "#E2E8F0";
                    },
                    paddingTop: function () {
                      return 5;
                    },
                    paddingBottom: function () {
                      return 5;
                    },
                  },
                },
                { text: "\n" },
                {
                  columns: [
                    { text: "", width: "*" },
                    {
                      width: 250,
                      table: {
                        widths: ["*", "auto"],
                        body: summaryRows,
                      },
                      layout: {
                        paddingTop: function () {
                          return 4;
                        },
                        paddingBottom: function () {
                          return 4;
                        },
                        hLineWidth: function (i, node) {
                          return i === node.table.body.length - 1 ? 1.5 : 0;
                        },
                        hLineColor: function () {
                          return primaryColor;
                        },
                      },
                    },
                  ],
                },
              ]
            : [],
          {
            text: "\n",
          },
          {
            unbreakable: true,
            style: "remittanceBox",
            table: {
              widths: ["*", 180], // Left side fills available space, right side allocates fixed space for the stamp
              body: [
                [
                  // Left Column: Banking Details
                  {
                    stack: [
                      { text: "REMITTANCE ADVICE", style: "remittanceTitle" },
                      {
                        text:
                          "Please use reference " +
                          (data?.invoice_no ||
                            invoiceProp.invoice_number ||
                            data?.internalRef ||
                            "N/A") +
                          " when making payment.",
                        style: "remittanceText",
                        bold: true,
                      },
                      {
                        text: `Bank: ${company?.bank || ""}`,
                        style: "remittanceText",
                      },
                      {
                        text: `Account Type: ${
                          company?.account_type ||
                          `${this.getTrustAccLabel.toUpperCase()} Account`
                        }`,
                        style: "remittanceText",
                      },
                      {
                        text: `Account Number: ${
                          company?.account_number || ""
                        }`,
                        style: "remittanceText",
                      },
                    ],
                  },
                  // Right Column: Company Stamp
                  {
                    stack: stampBase64
                      ? [
                          {
                            image: stampBase64,
                            width: 180, // Adjust width to fit your layout
                            alignment: "right",
                          },
                        ]
                      : [], // Renders nothing if stampBase64 is null
                    alignment: "right",
                    valign: "middle", // Vertically centers the stamp in the cell if supported by your pdfMake version
                  },
                ],
              ],
            },
            layout: {
              fillColor: function () {
                return "#F8FAFC";
              },
              hLineWidth: function () {
                return 1;
              },
              vLineWidth: function () {
                return 1;
              },
              hLineColor: function () {
                return "#CBD5E1";
              },
              vLineColor: function () {
                return "#CBD5E1";
              },
              paddingLeft: function () {
                return 12;
              },
              paddingRight: function () {
                return 12;
              },
              paddingTop: function () {
                return 10;
              },
              paddingBottom: function () {
                return 10;
              },
            },
          },
          // {
          //   unbreakable: true,
          //   style: "remittanceBox",
          //   table: {
          //     widths: ["*"],
          //     body: [
          //       [
          //         {
          //           stack: [
          //             { text: "REMITTANCE ADVICE", style: "remittanceTitle" },
          //             {
          //               text:
          //                 "Please use reference " +
          //                 (data?.invoice_no ||
          //                   invoiceProp.invoice_number ||
          //                   data?.internalRef ||
          //                   "N/A") +
          //                 " when making payment.",
          //               style: "remittanceText",
          //               bold: true,
          //             },
          //             {
          //               text: `Bank: ${company?.bank || ""}`,
          //               style: "remittanceText",
          //             },
          //             {
          //               text: `Account Type: ${
          //                 company?.account_type ||
          //                 `${this.getTrustAccLabel.toUpperCase()} Account`
          //               }`,
          //               style: "remittanceText",
          //             },
          //             {
          //               text: `Account Number: ${
          //                 company?.account_number || ""
          //               }`,
          //               style: "remittanceText",
          //             },
          //           ],
          //         },
          //       ],
          //     ],
          //   },
          //   layout: {
          //     fillColor: function () {
          //       return "#F8FAFC";
          //     },
          //     hLineWidth: function () {
          //       return 1;
          //     },
          //     vLineWidth: function () {
          //       return 1;
          //     },
          //     hLineColor: function () {
          //       return "#CBD5E1";
          //     },
          //     vLineColor: function () {
          //       return "#CBD5E1";
          //     },
          //     paddingLeft: function () {
          //       return 12;
          //     },
          //     paddingRight: function () {
          //       return 12;
          //     },
          //     paddingTop: function () {
          //       return 10;
          //     },
          //     paddingBottom: function () {
          //       return 10;
          //     },
          //   },
          // },
          {
            text: `Created With ${appName} [${appSite}]`,
            alignment: "center",
            style: "tearHereText",
            margin: [0, 15, 0, 5],
          },
        ],
        styles: {
          tearHereText: {
            fontSize: 9,
            italics: true,
          },
          sectionTitle: {
            fontSize: 10,
            bold: true,
            color: primaryColor,
            characterSpacing: 0.5,
            marginBottom: 6,
          },
          tableHeader: {
            fontSize: 8.5,
            bold: true,
            fillColor: primaryColor,
            color: "#FFFFFF",
            alignment: "left",
          },
          metaLabel: { fontSize: 8.5, color: "#64748B", bold: true },
          metaValue: { fontSize: 8.5, color: "#1E293B" },
          metaValueHighlight: {
            fontSize: 8.5,
            color: primaryColor,
            bold: true,
          },
          summaryLabel: { fontSize: 8.5, color: "#64748B" },
          summaryVal: { fontSize: 8.5, color: "#1E293B" },
          summaryBoldLabel: { fontSize: 9.5, bold: true, color: "#0F172A" },
          summaryBoldVal: { fontSize: 9.5, bold: true, color: primaryColor },
          trustSummaryLabel: { fontSize: 8.5, color: "#475569" },
          trustSummaryVal: { fontSize: 8.5, color: "#0F172A", bold: true },
          netDueLabel: { fontSize: 10, bold: true, color: primaryColor },
          netDueVal: { fontSize: 10, bold: true, color: primaryColor },
          remittanceTitle: {
            fontSize: 9.5,
            bold: true,
            color: primaryColor,
            marginBottom: 4,
          },
          remittanceText: { fontSize: 8.5, color: "#334155", leading: 12 },
        },
        defaultStyle: { fontSize: 8.5, font: "Roboto", color: "#1E293B" },
      };

      this.pdfDefinition = docDefinition;

      // Output strategy based on view mode
      if (this.$vuetify.breakpoint.smAndDown) {
        // Mobile: Open Vuetify Dialog & PDF.js rendering
        //   this.mobileDialog = true;

        // pdfMake.createPdf(this.pdfDefinition).getBlob((blob) => {
        //   this.pdfBlob = blob;
        //   const reader = new FileReader();
        //   reader.onload = async () => {
        //     const typedArray = new Uint8Array(reader.result);
        //     const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
        //     this.pdfDoc = pdf;
        //     this.totalPages = pdf.numPages;
        //     this.currentPage = 1;
        //     this.pdfReady = true;
        //     this.$nextTick(() => {
        //       this.renderPage();
        //     });
        //   };
        //   reader.readAsArrayBuffer(blob);
        // });

        const pdfDocGenerator = pdfMake.createPdf(docDefinition);
        pdfDocGenerator.download(`${this.title} - ${invoiceOrQuoteNumber}`);

        // this.mobileDialog = true;

        // pdfMake.createPdf(docDefinition).getBlob((blob) => {
        //       this.pdfBlob = blob;

        //       const reader = new FileReader();
        //       reader.onload = async () => {
        //         const typedArray = new Uint8Array(reader.result);

        //         const pdf = await pdfjsLib.getDocument({ data: typedArray })
        //           .promise;
        //         this.pdfDoc = pdf;
        //         this.totalPages = pdf.numPages;
        //         this.pdfReady = true;

        //         this.$nextTick(() => {
        //           this.renderPage();
        //         });
        //       };
        //       reader.readAsArrayBuffer(blob);
        //     });
      } else {
        pdfMake.createPdf(docDefinition).open();
      }
    },
    //       async generatePDF() {
    //         this.$store.commit('setLoader',true)
    //         const company = await companiesJs.initialiseCompanyAndLogo()
    //         this.$store.commit('setLoader',false)
    //         if(this.getAppModeFlags.itsLegal){
    //           const itsAnInvoice = this.invoice && this.invoice.quote_number ? false : true;
    //           if(itsAnInvoice){
    //               const allCases = this.getAllCases;
    //               if (!allCases || !allCases.length) {
    //                   return;
    //               }

    //               this.caseData = allCases.find(caseD => caseD.invoice_no == this.invoice.invoice_number);
    //               if (!this.caseData) {
    //                 return;
    //               }
    //           }else{
    //             this.caseData = this.invoice
    //           }
    //         }else if(this.getAppModeFlags.itsAuto){
    //           const itsAnInvoice = this.invoice && this.invoice.quote_number ? false : true;
    //           if(itsAnInvoice){
    //               const allAutoServices = this.getAllAutoServices;
    //               if (!allAutoServices || !allAutoServices.length) {
    //                   return;
    //               }

    //               this.caseData = allAutoServices.find(AutoServ => AutoServ.invoice_number == this.invoice.invoice_number);
    //               if (!this.caseData) {
    //                 return;
    //               }
    //           }else{
    //             this.caseData = this.invoice
    //           }

    //         }else if(this.getAppModeFlags.itsProjects || this.getAppModeFlags.itsEvents){
    //           const itsAnInvoice = this.invoice && this.invoice.quote_number ? false : true;
    //           if(itsAnInvoice){
    //               const allProjects = this.getAllProjects;
    //               if (!allProjects || !allProjects.length) {
    //                   return;
    //               }

    //               this.caseData = allProjects.find(ProjServ => ProjServ.invoice_number == this.invoice.invoice_number);
    //               if (!this.caseData) {
    //                 return;
    //               }
    //           }else{
    //             this.caseData = this.invoice
    //           }
    //         }else if(this.getAppModeFlags.itsPractice || this.getAppModeFlags.itsSocial){
    //           const itsAnInvoice = this.invoice && this.invoice.quote_number ? false : true;
    //           if(itsAnInvoice){
    //               const allPractices = this.getAllPractices;
    //               if (!allPractices || !allPractices.length) {
    //                   return;
    //               }

    //               this.caseData = allPractices.find(PracServ => PracServ.invoice_number == this.invoice.invoice_number);
    //               if (!this.caseData) {
    //                 return;
    //               }
    //           }else{
    //             this.caseData = this.invoice
    //           }
    //         }else{
    //           this.caseData = this.invoice
    //         }

    //       const data = this.caseData;

    //       const invoiceProp = this.invoice //full invoice with all properties whether legal case or general

    //       const trustLedger = invoiceProp.trust_ledger || [];
    //       const primaryColor = this.getPrimaryColor || '#1A365D'; // Modern deep navy accent
    //       // const company = this.getUserCompany;

    //       const item_date = data?.instruction_date || invoiceProp?.invoice_date || invoiceProp?.quote_date || data?.created_at || ''

    //       const header = await pdfMakeParts.generatePdfHeaderCompact(this.title, company, invoiceProp?.client, primaryColor,item_date);
    //       let subheader = {};

    //       if(this.getAppModeFlags.itsAuto){
    //         subheader = pdfMakeParts.generateClientSubHeaderAuto(this.title,data)
    //       }else if(this.getAppModeFlags.itsLegal){
    //         subheader = pdfMakeParts.generateClientSubHeaderCase(this.title,data)
    //       }else if(this.getAppModeFlags.itsProjects){
    //         subheader = pdfMakeParts.generateClientSubHeaderProject(this.title,data)
    //       }else if(this.getAppModeFlags.itsEvents){
    //         subheader = pdfMakeParts.generateClientSubHeaderEvent(this.title,data)
    //       }else if(this.getAppModeFlags.itsPractice){
    //         subheader = pdfMakeParts.generatePrivatePracticeSubHeader(this.title,data)
    //       }else if(this.getAppModeFlags.itsSocial){
    //         subheader = pdfMakeParts.generateSocialWorkSubHeader(this.title,data)
    //       }else{
    //         subheader = pdfMakeParts.generateClientSubHeaderGeneral(this.title,data)
    //       }

    //   // -------------------------------------------------------------
    //   // 1. BUILD BILLING ENTRIES TABLE
    //   // -------------------------------------------------------------
    //   var body = [
    //     [
    //       { text: 'DATE', style: 'tableHeader' },
    //       { text: 'DESCRIPTION / SERVICE', style: 'tableHeader' },
    //       { text: 'QTY', style: 'tableHeader', alignment: 'center' },
    //       { text: 'AMOUNT (EXCL)', style: 'tableHeader', alignment: 'right' },
    //       { text: 'VAT', style: 'tableHeader', alignment: 'right' },
    //       { text: 'AMOUNT', style: 'tableHeader', alignment: 'right' }
    //     ]
    //   ];

    //   var subTotalExcl = 0;
    //   var totalVAT = 0;
    //   var entries = data?.billingEntries || invoiceProp?.billing_entries || [];

    //   for (var i = 0; i < entries.length; i++) {
    //     var entry = entries[i];

    //     var rawDate = entry.created_at || entry.updated_at || '';
    //     var date = rawDate ? rawDate.split(' ')[0] : 'N/A';
    //     var description = entry.name || 'Service Item';
    //     var type = entry.item_type ? entry.item_type.toUpperCase() : 'FEE';
    //     var qty = Number(entry.quantity) || 1;

    //     var price = Number(entry.price) || 0;
    //     var lineTotal = Number(entry.lineTotal) || (price * qty);
    //     var vat = Number(entry.tax_amount) || 0;

    //     subTotalExcl += lineTotal;
    //     totalVAT += vat;

    //     var rowBg = (i % 2 === 0) ? '#FFFFFF' : '#F9FAFB';

    //     body.push([
    //       { text: date, fillColor: rowBg },
    //       { text: description, fillColor: rowBg, bold: true },
    //       { text: qty.toString(), alignment: 'center', fillColor: rowBg },
    //       { text: this.formatCurrency(price), alignment: 'right', fillColor: rowBg },
    //       { text: this.formatCurrency(vat), alignment: 'right', fillColor: rowBg },
    //       { text: this.formatCurrency(lineTotal + vat), alignment: 'right', fillColor: rowBg, bold: true }
    //     ]);
    //   }

    //   const grandTotalBilled = subTotalExcl + totalVAT;

    //   // -------------------------------------------------------------
    //   // 2. DYNAMIC TRUST LEDGER CALCULATIONS
    //   // -------------------------------------------------------------
    //   var trustBody = [
    //     [
    //       { text: 'DATE', style: 'tableHeader' },
    //       { text: 'DESCRIPTION', style: 'tableHeader' },
    //       { text: 'METHOD', style: 'tableHeader' },
    //       { text: 'TYPE', style: 'tableHeader' },
    //       { text: 'AMOUNT', style: 'tableHeader', alignment: 'right' }
    //     ]
    //   ];

    //   let calculatedTrustBalance = 0;

    //   for (var j = 0; j < trustLedger.length; j++) {
    //     var ledgerItem = trustLedger[j];
    //     var ledgerDate = ledgerItem.transaction_at ? ledgerItem.transaction_at.split(' ')[0] : 'N/A';
    //     var itemAmount = Number(ledgerItem.amount) || 0;
    //     var itemType = (ledgerItem.type || '').trim().toLowerCase();

    //     // Dynamically update trust balance based on transaction type
    //     if (itemType === 'deposit' || itemType === 'credit') {
    //       calculatedTrustBalance += itemAmount;
    //     } else if (['disbursement', 'withdrawal', 'transfer', 'debit','fee'].includes(itemType)) {
    //       calculatedTrustBalance -= itemAmount;
    //     } else {
    //       // Fallback: If type is unspecified, default positive addition
    //       calculatedTrustBalance += itemAmount;
    //     }

    //     var trustRowBg = (j % 2 === 0) ? '#FFFFFF' : '#F9FAFB';

    //     trustBody.push([
    //       { text: ledgerDate, fillColor: trustRowBg },
    //       { text: ledgerItem.description || 'N/A', fillColor: trustRowBg },
    //       { text: ledgerItem.payment_method || 'N/A', fillColor: trustRowBg },
    //       { text: ledgerItem.type || 'N/A', fillColor: trustRowBg, bold: true },
    //       { text: this.formatCurrency(itemAmount), alignment: 'right', fillColor: trustRowBg }
    //     ]);
    //   }

    //   // Calculate net amount due after utilizing available trust balance
    //   // -------------------------------------------------------------
    // // DYNAMIC FINANCIAL CALCULATIONS
    // // -------------------------------------------------------------
    // // Direct payments made directly against this invoice (out-of-pocket)
    // const trustLabel = (this.getTrustAccLabel + ' Account').toLowerCase() ;

    // const directPayments = (invoiceProp?.payments || data.all_payments || [])
    //   .filter(pay => !pay.payment_method?.toLowerCase() === trustLabel)
    //   .reduce((acc, pay) => acc + (Number(pay.amount_paid) || 0), 0);

    // const trustApplied = (invoiceProp.payments || data.all_payments || [])
    //   .filter(pay => pay.payment_method?.toLowerCase() === trustLabel)
    //   .reduce((acc, pay) => acc + (Number(pay.amount_paid) || 0), 0);

    // const remainingUnpaid = Math.max(0, (grandTotalBilled - directPayments - trustApplied));

    // // Trust offset applied ONLY up to the remaining unpaid amount
    // //const trustApplied = Math.min(calculatedTrustBalance, remainingUnpaid);

    // // Final net amount the client actually needs to pay out-of-pocket
    // const netAmountDue = Math.max(0, remainingUnpaid);

    // // Client's left-over trust balance after this invoice is settled
    // const remainingTrust = Math.max(0, calculatedTrustBalance);

    // const summaryRows = [
    //   [{ text: 'Subtotal (Excl VAT):', style: 'summaryLabel' }, { text: this.formatCurrency(subTotalExcl), alignment: 'right', style: 'summaryVal' }],
    //   [{ text: 'Total VAT (15%):', style: 'summaryLabel' }, { text: this.formatCurrency(totalVAT), alignment: 'right', style: 'summaryVal' }],
    //   [{ text: `TOTAL ${this.title.toLowerCase().includes('invoice') ? 'INVOICE' : 'QUOTATION'} AMOUNT:`, style: 'summaryBoldLabel' }, { text: this.formatCurrency(grandTotalBilled), alignment: 'right', style: 'summaryBoldVal' }]
    // ];

    // // If out-of-pocket payments were received
    // if (directPayments > 0) {
    //   summaryRows.push([
    //     { text: 'Direct Payments Received:', style: 'summaryLabel' },
    //     { text: '- ' + this.formatCurrency(directPayments), alignment: 'right', style: 'summaryVal' }
    //   ]);
    // }

    // // If trust funds were applied
    // if (trustApplied > 0) {
    //   summaryRows.push([
    //     { text: `${(this.getTrustAccLabel).toUpperCase()} Account Funds Applied:`, style: 'trustSummaryLabel' },
    //     { text: '- ' + this.formatCurrency(trustApplied), alignment: 'right', style: 'trustSummaryVal' }
    //   ]);
    // }

    // // Final Balance Due
    // summaryRows.push([
    //   { text: 'NET AMOUNT DUE FROM CLIENT:', style: 'netDueLabel' },
    //   { text: this.formatCurrency(netAmountDue), alignment: 'right', style: 'netDueVal' }
    // ]);

    // // Display remaining trust balance if trust account was involved
    // if (calculatedTrustBalance > 0) {
    //   summaryRows.push([
    //     { text: `REMAINING ${(this.getTrustAccLabel).toUpperCase()} BALANCE:`, style: 'trustSummaryLabel', bold: true },
    //     { text: this.formatCurrency(remainingTrust), alignment: 'right', style: 'trustSummaryVal', bold: true }
    //   ]);
    // }

    //   // -------------------------------------------------------------
    //   // 3. DOCUMENT DEFINITION (PDF MAKE)
    //   // -------------------------------------------------------------
    //   const docDefinition = {
    //     pageSize: 'A4',
    //     pageMargins: [35, 45, 35, 50],
    //     content: [
    //       header,

    //       // Accent Divider Line
    //       { canvas: [{ type: 'line', x1: 0, y1: 8, x2: 525, y2: 8, lineWidth: 1.5, lineColor: primaryColor }] },
    //       { text: '\n' },

    //       // Structured Matter Metadata Box
    //       subheader,
    //       // {
    //       //   style: 'metadataCard',
    //       //   table: {
    //       //     widths: ['16%', '34%', '16%', '34%'],
    //       //     body: [
    //       //       [
    //       //         { text: 'Client Name:', style: 'metaLabel' }, { text: data?.clientName || data?.client?.name || '', style: 'metaValue' },
    //       //         { text: `${this.title.toLowerCase().includes('invoice') ? 'Invoice' : 'Quotation'} Number:`, style: 'metaLabel' }, { text: data?.invoice_no || data?.invoice_number || '', style: 'metaValueHighlight' }
    //       //       ],
    //       //       [
    //       //         { text: 'Client Ref:', style: 'metaLabel' }, { text: data?.clientRef || data?.client?.client_reference || '', style: 'metaValue' },
    //       //         { text: itsLegal ? 'Internal Ref:' : 'Address:', style: 'metaLabel' }, { text: itsLegal ? data?.internalRef || 'N/A' : `${data?.client?.address || ''}, ${data?.client?.city || ''}` || '', style: 'metaValue' }
    //       //       ],
    //       //       [
    //       //         { text: itsLegal ? 'Court Case No:' : 'Entity:', style: 'metaLabel' }, { text: itsLegal ? data?.courtCaseNo || 'N/A' : data?.client?.institution || '', style: 'metaValue' },
    //       //         { text: itsLegal ? 'SAPS Case No:' : 'Phone:' , style: 'metaLabel' }, { text: itsLegal ? data?.sapsCaseNo || 'N/A' : data?.client?.phone, style: 'metaValue' }
    //       //       ],
    //       //       [
    //       //         { text: itsLegal ? 'Seat of Court:' : 'Position:', style: 'metaLabel' }, { text: itsLegal ? data?.seat_of_court || 'N/A' : data?.client?.position || '', style: 'metaValue' },
    //       //         { text: itsLegal ? 'Instruction Date:' : `${this.title.toLowerCase().includes('invoice') ? 'Invoice' : 'Quotation'} Date:`, style: 'metaLabel' }, { text: itsLegal ? data?.instruction_date || 'N/A' : data?.invoice_date || '', style: 'metaValue' }
    //       //       ]
    //       //     ]
    //       //   },
    //       //   layout: {
    //       //     hLineWidth: function() { return 0.5; },
    //       //     vLineWidth: function() { return 0; },
    //       //     hLineColor: function() { return '#E2E8F0'; },
    //       //     paddingLeft: function() { return 6; },
    //       //     paddingRight: function() { return 6; },
    //       //     paddingTop: function() { return 4; },
    //       //     paddingBottom: function() { return 4; }
    //       //   }
    //       // },

    //       { text: '\n' },
    //       { text: 'FEE & BILLING BREAKDOWN', style: 'sectionTitle' },

    //       // Billing Entries Table
    //       {
    //         table: {
    //           headerRows: 1,
    //           widths: ['16%', '*', '8%', '18%', '13%', '22%'],
    //           body: body
    //         },
    //         layout: {
    //           hLineWidth: function(i, node) { return (i === 0 || i === node.table.body.length) ? 1 : 0.5; },
    //           vLineWidth: function() { return 0; },
    //           hLineColor: function() { return '#E2E8F0'; },
    //           paddingTop: function() { return 6; },
    //           paddingBottom: function() { return 6; }
    //         }
    //       },

    //       { text: '\n' },

    //       // Totals & Financial Summary Card
    //       {
    //         columns: [
    //           { text: '', width: '*' },
    //           {
    //             width: 240,
    //             table: {
    //               widths: ['*', 'auto'],
    //               body: [
    //                 [{ text: 'Subtotal (Excl VAT):', style: 'summaryLabel' }, { text: this.formatCurrency(subTotalExcl), alignment: 'right', style: 'summaryVal' }],
    //                 [{ text: 'Total VAT (15%):', style: 'summaryLabel' }, { text: this.formatCurrency(totalVAT), alignment: 'right', style: 'summaryVal' }],
    //                 [
    //                   { text: `TOTAL ${this.title.toLowerCase().includes('invoice') ? 'INVOICE' : 'QUOTATION'} AMOUNT:`, style: 'summaryBoldLabel' },
    //                   { text: this.formatCurrency(grandTotalBilled), alignment: 'right', style: 'summaryBoldVal' }
    //                 ]
    //               ]
    //             },
    //             layout: {
    //               paddingTop: function() { return 4; },
    //               paddingBottom: function() { return 4; },
    //               hLineWidth: function(i, node) { return (i === node.table.body.length - 1) ? 1 : 0; },
    //               hLineColor: function() { return '#CBD5E1'; }
    //             }
    //           }
    //         ]
    //       },

    //       // Trust Ledger Statement (Rendered dynamically if entries exist)
    //       trustLedger.length ? [
    //         { text: '\n\n' },
    //         { text: `${(this.getTrustAccLabel).toUpperCase()} ACCOUNT LEDGER STATEMENT`, style: 'sectionTitle' },
    //         {
    //           table: {
    //             headerRows: 1,
    //             widths: ['15%', '*', '18%', '15%', '20%'],
    //             body: trustBody
    //           },
    //           layout: {
    //             hLineWidth: function(i, node) { return (i === 0 || i === node.table.body.length) ? 1 : 0.5; },
    //             vLineWidth: function() { return 0; },
    //             hLineColor: function() { return '#E2E8F0'; },
    //             paddingTop: function() { return 5; },
    //             paddingBottom: function() { return 5; }
    //           }
    //         },
    //         { text: '\n' },
    //         {
    //           columns: [
    //             { text: '', width: '*' },
    //             {
    //               width: 250,
    //               table: {
    //                 widths: ['*', 'auto'],
    //                 body: summaryRows
    //               },
    //               layout: {
    //                 paddingTop: function() { return 4; },
    //                 paddingBottom: function() { return 4; },
    //                 hLineWidth: function(i, node) { return (i === node.table.body.length - 1) ? 1.5 : 0; },
    //                 hLineColor: function() { return primaryColor; }
    //               }
    //             }
    //           ]
    //         }
    //       ] : [],

    //       // Remittance Advice Box
    //       { text: '\n\n' },
    //       {
    //         unbreakable: true,
    //         style: 'remittanceBox',
    //         table: {
    //           widths: ['*'],
    //           body: [
    //             [
    //               {
    //                 stack: [
    //                   { text: 'REMITTANCE ADVICE', style: 'remittanceTitle' },
    //                   { text: 'Please use reference ' + ( data?.invoice_no || invoiceProp.invoice_number || data?.internalRef || 'N/A') + ' when making payment.', style: 'remittanceText', bold: true },
    //                   { text: `Bank: ${company?.bank || ''}`, style: 'remittanceText' },
    //                   { text: `Account Type: ${company?.account_type || `${(this.getTrustAccLabel).toUpperCase()} Account`}`, style: 'remittanceText' },
    //                   { text: `Account Number: ${company?.account_number || ''}`, style: 'remittanceText' }
    //                 ]
    //               }
    //             ]
    //           ]
    //         },
    //         layout: {
    //           fillColor: function() { return '#F8FAFC'; },
    //           hLineWidth: function() { return 1; },
    //           vLineWidth: function() { return 1; },
    //           hLineColor: function() { return '#CBD5E1'; },
    //           vLineColor: function() { return '#CBD5E1'; },
    //           paddingLeft: function() { return 12; },
    //           paddingRight: function() { return 12; },
    //           paddingTop: function() { return 10; },
    //           paddingBottom: function() { return 10; }
    //         }
    //       }
    //     ],

    //     // -------------------------------------------------------------
    //     // STYLES & TYPOGRAPHY
    //     // -------------------------------------------------------------
    //     styles: {
    //       sectionTitle: { fontSize: 10, bold: true, color: primaryColor, characterSpacing: 0.5, marginBottom: 6 },
    //       tableHeader: { fontSize: 8.5, bold: true, fillColor: primaryColor, color: '#FFFFFF', alignment: 'left' },
    //       metaLabel: { fontSize: 8.5, color: '#64748B', bold: true },
    //       metaValue: { fontSize: 8.5, color: '#1E293B' },
    //       metaValueHighlight: { fontSize: 8.5, color: primaryColor, bold: true },
    //       summaryLabel: { fontSize: 8.5, color: '#64748B' },
    //       summaryVal: { fontSize: 8.5, color: '#1E293B' },
    //       summaryBoldLabel: { fontSize: 9.5, bold: true, color: '#0F172A' },
    //       summaryBoldVal: { fontSize: 9.5, bold: true, color: primaryColor },
    //       trustSummaryLabel: { fontSize: 8.5, color: '#475569' },
    //       trustSummaryVal: { fontSize: 8.5, color: '#0F172A', bold: true },
    //       netDueLabel: { fontSize: 10, bold: true, color: primaryColor },
    //       netDueVal: { fontSize: 10, bold: true, color: primaryColor },
    //       remittanceTitle: { fontSize: 9.5, bold: true, color: primaryColor, marginBottom: 4 },
    //       remittanceText: { fontSize: 8.5, color: '#334155', leading: 12 }
    //     },
    //     defaultStyle: { fontSize: 8.5, font: 'Roboto', color: '#1E293B' }
    //   };

    //      //store document definition
    //      this.pdfDefinition = docDefinition;
    //         // Step 4: Generate and assign PDF data URL

    //         if (this.isMobile) {
    //           //Cluade method for mobile rendering
    //           pdfMake.createPdf(this.pdfDefinition).getBlob((blob) => {
    //             this.pdfBlob = blob;

    //             const reader = new FileReader();
    //             reader.onload = async () => {
    //               const typedArray = new Uint8Array(reader.result);

    //               const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
    //               this.pdfDoc = pdf;
    //               this.totalPages = pdf.numPages;
    //               this.pdfReady = true;
    //               this.renderPage();
    //             };
    //             reader.readAsArrayBuffer(blob);
    //           });
    //         } else {
    //           // this.pdfContent Insert AI-generated content here
    //           const pdfDoc = pdfMake.createPdf(this.pdfDefinition);
    //           pdfDoc.getBlob((blob) => {
    //             this.pdfDataUrl = URL.createObjectURL(blob);
    //           });
    //         }
    //   //pdfMake.createPdf(docDefinition).open();
    // },
    goBack() {
      this.$router.go(-1);
    },
    downloadPdf() {
      try {
        // Generate PDF as data URL directly
        const pdfDocGenerator = pdfMake.createPdf(this.pdfDefinition);
        pdfDocGenerator.download(this.title);
      } catch (error) {
        console.error("PDF generation error:", error);
        alert("Failed to generate PDF. See console for details.");
      }
    },
    renderPage() {
      if (!this.pdfDoc) return;

      this.pdfDoc.getPage(this.currentPage).then((page) => {
        const viewport = page.getViewport({ scale: this.zoom });
        const canvas = this.$refs.pdfCanvas;
        const context = canvas.getContext("2d");

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        page.render(renderContext);
      });
    },
    //     renderPage() {
    //   if (!this.pdfDoc) return;

    //   // 1. Cancel any active render task to avoid canvas collisions
    //   if (this.renderTask) {
    //     this.renderTask.cancel();
    //     this.renderTask = null;
    //   }

    //   this.pdfDoc.getPage(this.currentPage).then((page) => {
    //     const canvas = this.$refs.pdfCanvas;
    //     if (!canvas) return;

    //     const context = canvas.getContext("2d");
    //     const dpr = window.devicePixelRatio || 1;

    //     // 2. Calculate scaled viewport
    //     const viewport = page.getViewport({ scale: this.zoom });

    //     // 3. Set actual canvas resolution for High-DPI / Retina displays
    //     canvas.height = Math.floor(viewport.height * dpr);
    //     canvas.width = Math.floor(viewport.width * dpr);

    //     // 4. Set CSS display size to match logical viewport
    //     canvas.style.width = `${Math.floor(viewport.width)}px`;
    //     canvas.style.height = `${Math.floor(viewport.height)}px`;

    //     // 5. Scale the 2D context to match DPR
    //     const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null;

    //     const renderContext = {
    //       canvasContext: context,
    //       viewport: viewport,
    //       transform: transform
    //     };

    //     // 6. Store reference to current render task
    //     this.renderTask = page.render(renderContext);

    //     this.renderTask.promise
    //       .then(() => {
    //         this.renderTask = null;
    //       })
    //       .catch((error) => {
    //         // Ignore expected cancellation errors during quick navigation/zooming
    //         if (error?.name !== "RenderingCancelledException") {
    //           console.error("PDF rendering error:", error);
    //         }
    //       });
    //   });
    // },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.renderPage();
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderPage();
      }
    },

    zoomIn() {
      if (this.zoom < 2) {
        this.zoom += 0.1;
        this.renderPage();
      }
    },

    zoomOut() {
      if (this.zoom > 0.5) {
        this.zoom -= 0.1;
        this.renderPage();
      }
    },
  },
};
</script>

<style scoped>
/* No UI styles needed as this is a logic-gate component */
</style>
